// ============================================================
// 异步任务轮询 store（全局单例，跨路由 / 跨刷新存活）
//
// 两级 localStorage 队列：
// - asyncTasks:active  快轮询（默认每 3s 心跳一次），超过 activeWindowMs（默认 5min）仍未完成则转入 waiting
// - asyncTasks:waiting 慢速抽查（默认每 waitCheckIntervalMs 10min 查一次），超过 maxWaitMs（默认 2h）
//   仍未完成则前端放弃：按任务类型的 notifyOnGiveUp 决定是否弹失败提示框，然后彻底丢弃、不再追踪
//
// 心跳调度：标签页可见用 requestAnimationFrame，隐藏时自动降级为 setTimeout，
// 两者共用同一个基于 performance.now() 的到点判断，避免切标签页导致轮询完全停摆
//
// 所有超时 / 到点判断都基于持久化的时间戳与 Date.now()/performance.now() 现算，
// 不依赖内存里的计时累计，因此刷新页面、关闭浏览器几小时后再打开都能正确续接或正确放弃
// ============================================================
import { defineStore } from "pinia";
import { ElMessage } from "element-plus";
import { storage } from "@shared/utils/storage";
import { fetchAsyncTaskBatchDetail } from "@shared/api/asyncTask";
import { AsyncTaskStatus } from "@shared/types/asyncTask";
import type { ActiveAsyncTask, AsyncTaskContext, AsyncTaskDetail, WaitingAsyncTask } from "@shared/types/asyncTask";
import { getAsyncTaskHandler, getAsyncTaskPollingConfig } from "@shared/utils/asyncTaskHandlers";
import { notifyTaskFailed, notifyTaskSuccess } from "@shared/utils/asyncTaskNotify";
import { t } from "@shared/utils/i18n";

const ACTIVE_KEY = "asyncTasks:active";
const WAITING_KEY = "asyncTasks:waiting";
const TICK_INTERVAL_MS = 3000;

type TickMode = "raf" | "timeout";

export const useAsyncTaskStore = defineStore("asyncTask", () => {
    let basePath = "";

    let rafId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let currentMode: TickMode | null = null;
    let ticking = false;
    let isTickRunning = false;
    let lastTickAt = 0;
    let visibilityBound = false;

    /** 各端 App 启动时调用一次，注入本端接口前缀（/manage、/customer……） */
    function configure(options: { basePath: string }) {
        basePath = options.basePath;
    }

    // ---------- 读写两个队列 ----------
    const readActive = (): ActiveAsyncTask[] => storage.get<ActiveAsyncTask[]>(ACTIVE_KEY) ?? [];
    const writeActive = (list: ActiveAsyncTask[]) => storage.set(ACTIVE_KEY, list);
    const readWaiting = (): WaitingAsyncTask[] => storage.get<WaitingAsyncTask[]>(WAITING_KEY) ?? [];
    const writeWaiting = (list: WaitingAsyncTask[]) => storage.set(WAITING_KEY, list);

    /** 把 batchDetail 里的展示名写回队列条目，供 2h 放弃时使用 */
    function withCachedLabel<T extends ActiveAsyncTask | WaitingAsyncTask>(entry: T, task?: AsyncTaskDetail): T {
        const label = task?.taskTypeLabel?.trim();
        if (!label) return entry;
        return { ...entry, taskTypeLabel: label };
    }

    /** 命中终态则弹对应通知并返回 true；调用方据此把任务从队列里摘掉 */
    function settleIfTerminal(taskType: string, context: AsyncTaskContext, task?: AsyncTaskDetail): boolean {
        if (!task) return false; // 批量结果里没查到这条：当作还没出结果，留在原队列
        const handler = getAsyncTaskHandler(taskType);
        if (task.status === AsyncTaskStatus.Success) {
            if (handler) notifyTaskSuccess(handler.buildSuccess(task, context));
            return true;
        }
        if (task.status === AsyncTaskStatus.Failed) {
            if (handler) notifyTaskFailed(handler.buildFailed(task, context));
            return true;
        }
        return false; // 1 待处理 / 2 执行中：继续留在队列
    }

    async function batchFetch(taskIds: string[]): Promise<Map<string, AsyncTaskDetail>> {
        if (!taskIds.length) return new Map();
        try {
            const res = await fetchAsyncTaskBatchDetail(basePath, taskIds);
            return new Map((res.items ?? []).map(item => [item.taskId, item]));
        } catch {
            return new Map(); // 整批网络异常：这次心跳当作"都还没出结果"，下次 tick 再试
        }
    }

    async function runTick() {
        if (isTickRunning) return;
        isTickRunning = true;
        try {
            const now = Date.now();
            const activeList = readActive();
            const waitingList = readWaiting();

            const isExpiredActive = (item: ActiveAsyncTask) => now - item.activeEnteredAt >= getAsyncTaskPollingConfig(item.taskType).activeWindowMs;
            const stillActive = activeList.filter(item => !isExpiredActive(item));
            const expiredActive = activeList.filter(isExpiredActive);

            const isDueWaiting = (item: WaitingAsyncTask) => now - item.lastCheckedAt >= getAsyncTaskPollingConfig(item.taskType).waitCheckIntervalMs;
            const dueWaiting = waitingList.filter(isDueWaiting);
            const notDueWaiting = waitingList.filter(item => !isDueWaiting(item));

            // 核心：active 全量 + 到期的 waiting，合并成一次 batchDetail 请求
            const batchIds = [...stillActive, ...expiredActive, ...dueWaiting].map(item => item.taskId);
            const resultMap = await batchFetch(batchIds);

            const nextActive: ActiveAsyncTask[] = [];
            const nextWaiting: WaitingAsyncTask[] = [...notDueWaiting];

            for (const entry of stillActive) {
                const task = resultMap.get(entry.taskId);
                if (!settleIfTerminal(entry.taskType, entry.context, task)) {
                    nextActive.push(withCachedLabel(entry, task));
                }
            }
            for (const entry of expiredActive) {
                const task = resultMap.get(entry.taskId);
                if (settleIfTerminal(entry.taskType, entry.context, task)) continue;
                // active 窗口到期仍未出结果：转入 waiting，重新计时；不透传 activeEnteredAt（已随 active 阶段结束失效）
                nextWaiting.push(
                    withCachedLabel(
                        {
                            taskId: entry.taskId,
                            taskType: entry.taskType,
                            context: entry.context,
                            userId: entry.userId,
                            taskTypeLabel: entry.taskTypeLabel,
                            createdAt: entry.createdAt,
                            enteredWaitingAt: now,
                            lastCheckedAt: now,
                        },
                        task,
                    ),
                );
            }
            for (const entry of dueWaiting) {
                const task = resultMap.get(entry.taskId);
                if (settleIfTerminal(entry.taskType, entry.context, task)) continue;
                const cached = withCachedLabel(entry, task);
                const { maxWaitMs, notifyOnGiveUp } = getAsyncTaskPollingConfig(entry.taskType);
                if (now - entry.enteredWaitingAt >= maxWaitMs) {
                    if (notifyOnGiveUp) {
                        const handler = getAsyncTaskHandler(entry.taskType);
                        if (handler) {
                            const label = cached.taskTypeLabel || handler.taskTypeLabel || entry.taskType;
                            notifyTaskFailed(
                                handler.buildGiveUp?.(entry.context, cached.taskTypeLabel) ?? {
                                    title: t("任务执行失败"),
                                    segments: [`${label}${t("任务执行失败")}`],
                                },
                            );
                        }
                    }
                    continue; // 无论是否弹提示，都从 waiting 里移除，不再追踪
                }
                nextWaiting.push({ ...cached, lastCheckedAt: now });
            }

            writeActive(nextActive);
            writeWaiting(nextWaiting);

            if (nextActive.length === 0 && nextWaiting.length === 0) {
                stopTicking();
            }
        } finally {
            isTickRunning = false;
        }
    }

    // ---------- 心跳调度：前台 rAF / 后台 setTimeout 混合 ----------
    function frameLoop(now: number) {
        if (!ticking || currentMode !== "raf") return;
        if (now - lastTickAt >= TICK_INTERVAL_MS && !isTickRunning) {
            lastTickAt = now;
            void runTick();
        }
        rafId = requestAnimationFrame(frameLoop);
    }

    function timeoutLoop() {
        if (!ticking || currentMode !== "timeout") return;
        lastTickAt = performance.now();
        if (!isTickRunning) void runTick();
        timeoutId = setTimeout(timeoutLoop, TICK_INTERVAL_MS);
    }

    function stopSchedulers() {
        if (rafId != null) cancelAnimationFrame(rafId);
        if (timeoutId != null) clearTimeout(timeoutId);
        rafId = null;
        timeoutId = null;
    }

    function startMode(mode: TickMode) {
        stopSchedulers();
        currentMode = mode;
        if (mode === "raf") {
            rafId = requestAnimationFrame(frameLoop);
        } else {
            timeoutId = setTimeout(timeoutLoop, TICK_INTERVAL_MS);
        }
    }

    function ensureTicking() {
        bindVisibilityListener();
        if (ticking) return;
        ticking = true;
        lastTickAt = performance.now();
        void runTick(); // 启动时立即查一次，不管当前是前台还是后台
        startMode(typeof document !== "undefined" && document.hidden ? "timeout" : "raf");
    }

    function stopTicking() {
        ticking = false;
        stopSchedulers();
        currentMode = null;
    }

    function handleVisibilityChange() {
        if (!ticking) return;
        const nextMode: TickMode = document.hidden ? "timeout" : "raf";
        if (nextMode === currentMode) return;
        startMode(nextMode);
    }

    function bindVisibilityListener() {
        if (visibilityBound || typeof document === "undefined") return;
        visibilityBound = true;
        document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    // ---------- 对外 API ----------

    /** 触发动作拿到 taskId 后调用：写入 active 队列，弹一次提交 toast，并确保心跳在跑 */
    function register(taskType: string, taskId: string, context: AsyncTaskContext, userId?: string) {
        if (readActive().some(item => item.taskId === taskId) || readWaiting().some(item => item.taskId === taskId)) return;
        const handler = getAsyncTaskHandler(taskType);
        const now = Date.now();
        writeActive([...readActive(), { taskId, taskType, context, userId, createdAt: now, activeEnteredAt: now }]);
        if (handler) ElMessage.success(handler.buildPendingToast(context));
        ensureTicking();
    }

    /** App 启动 / 登录完成后调用一次：按当前用户过滤残留任务，续上心跳 */
    function restore(currentUserId?: number | string) {
        const belongsToUser = (userId?: string) => userId == null || currentUserId == null || userId === currentUserId;
        writeActive(readActive().filter(item => belongsToUser(item.userId)));
        writeWaiting(readWaiting().filter(item => belongsToUser(item.userId)));
        if (readActive().length || readWaiting().length) ensureTicking();
    }

    return { configure, register, restore };
});
